<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, inject, ref, watch } from 'vue';
import { CrmRelationsRelatieContext } from '@services/crm-public/orval-api/model/crmRelationsRelatieContext';
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useField } from 'vee-validate';

import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import { useRelatieStuurgegevensStore } from '../../relatieStuurGegevensStore';
import { isDisabledInjectionKey, isReadonlyInjectionKey } from '../keys';

const relatieStuurGegevensStore = useRelatieStuurgegevensStore();
const { relatieContext } = storeToRefs(relatieStuurGegevensStore);

const { value: hasDisabledParent } = inject(isDisabledInjectionKey, { value: ref(false) });
const { value: hasReadonlyParent } = inject(isReadonlyInjectionKey, { value: ref(false) });

const geboortedatumInitialValue = ref<Date | undefined>();
const geboortedatumIsRequired = computed(
  () =>
    relatieContext.value === CrmRelationsRelatieContext.Aanvraag ||
    !!geboortedatumInitialValue.value,
);
const geboortedatumValidation = computed(() => {
  const validation = z.date().max(new Date());
  return toTypedSchema(geboortedatumIsRequired.value ? validation : validation.optional());
});
const geboortedatum = useField<Date | undefined>('geboortedatum', geboortedatumValidation);

watch(
  () => geboortedatum.meta.initialValue,
  (fieldValue) => (geboortedatumInitialValue.value = fieldValue),
  { immediate: true },
);
</script>

<template>
  <FieldRowV2
    :errors="geboortedatum.errors.value"
    label="Geboortedatum"
    :required="!hasReadonlyParent && geboortedatumIsRequired"
    v-bind="$attrs"
  >
    <VDatePicker
      v-model="geboortedatum.value.value"
      :max-date="new Date()"
      :disabled="hasDisabledParent"
      :readonly="hasReadonlyParent"
      :invalid="!!geboortedatum.errors.value.length"
      id="relatiegegevens-geboortedatum"
    />
  </FieldRowV2>
</template>
